import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Pencil, FileText, Download, History, ChevronDown, Link2, Check, Lock, ShieldAlert, Star, UserCog, Zap, TrendingDown, TrendingUp, Percent, FileStack, Eye, Info, Plus, GitBranch, Tag, CreditCard } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { generatePdfCotizacion } from '../utils/pdfGenerator';
import { totalConRentabilidad, gananciaConRentabilidad, rowTotal, rowGanancia, rowTotalConVariaciones, rowGananciaConVariaciones, aplicarVariaciones, aplicarOferta, etiquetaOferta, calcularMontosPlanPago, hasOverride, formatDateTimeShort, getEstadoColor } from '../utils/helpers';
import { getCachedTiposServicio } from '../services/cachedCatalogs';
import { Button, Card, Modal, Tooltip, useConfirm } from './ui';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useCotizacionLock } from '../hooks/useCotizacionLock';
import PagosCard from './PagosCard';
import VariacionesModal from './VariacionesModal';
import HiloVersionesModal from './HiloVersionesModal';
import AnexosSection from './AnexosSection';
import AccesoDenegado from './AccesoDenegado';

const ESTADO_LABELS = {
    borrador: 'Borrador',
    enviada: 'Enviada',
    aprobada: 'Aprobada',
    rechazada: 'Rechazada'
};

const formatearFecha = (fecha) => {
    if (!fecha) return '';
    if (typeof fecha === 'string' && fecha.match(/^\d{4}-\d{2}-\d{2}/)) {
        const [a, m, d] = fecha.split('T')[0].split('-');
        return `${d}/${m}/${a}`;
    }
    const d = new Date(fecha);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatCurrency = (n, moneda = 'PEN') => {
    const symbol = moneda === 'USD' ? '$ ' : 'S/ ';
    return symbol + (parseFloat(n) || 0).toLocaleString('es-PE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

/**
 * Indicador read-only de variaciones aplicadas — click abre modal con la lista
 * completa (mismo modal que en edicion pero readOnly).
 * Con >2 variaciones un tooltip se satura, mejor modal dedicado.
 * Ícono según efecto neto (descuento/extra/mixto).
 */
const VariacionesReadOnly = ({ variaciones, onOpen }) => {
    const count = Array.isArray(variaciones) ? variaciones.length : 0;
    if (count === 0) return null;
    const sumaExtras = variaciones.filter(v => v.tipo === 'extra').length;
    const sumaDesc = variaciones.filter(v => v.tipo === 'descuento').length;
    const netEffect = sumaExtras > 0 && sumaDesc > 0 ? 'mixto' : sumaDesc > 0 ? 'descuento' : 'extra';
    const iconMap = { descuento: TrendingDown, extra: TrendingUp, mixto: Percent };
    const Icon = iconMap[netEffect];
    const colorMap = {
        descuento: 'rgb(var(--danger))',
        extra: 'rgb(var(--success))',
        mixto: 'rgb(var(--primary))'
    };
    return (
        <Tooltip content={`Ver ${count} variación${count === 1 ? '' : 'es'} aplicada${count === 1 ? '' : 's'}`}>
            <button
                type="button"
                onClick={onOpen}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    marginLeft: 6, padding: '2px 6px',
                    borderRadius: 4, fontSize: 10, fontWeight: 600,
                    color: colorMap[netEffect],
                    background: `${colorMap[netEffect]}20`,
                    border: `1px solid ${colorMap[netEffect]}40`,
                    cursor: 'pointer'
                }}
                aria-label="Ver variaciones aplicadas"
            >
                <Icon size={10} />
                {count}
            </button>
        </Tooltip>
    );
};

const CotizacionDetalle = ({ configuracion }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { tienePermiso, user } = useAuth();
    const confirm = useConfirm();
    const verDetalles = tienePermiso('ver_detalles_costos');
    const verPdf = tienePermiso('ver_pdf');
    // Wrapper de formatCurrency que respeta el permiso — si no puede ver precios,
    // muestra ••••• en lugar de "S/ 0.00" (backend ya envia null, esto solo hace
    // el render mas honesto sobre lo que esta oculto).
    const blurMoney = (n, moneda) => verDetalles ? formatCurrency(n, moneda) : '•••••';
    const puedeEditar = tienePermiso('gestion_cotizaciones') || tienePermiso('cotizaciones_asignadas');
    const puedeForzarLock = tienePermiso('forzar_lock_cotizacion');

    const [cotizacion, setCotizacion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accesoDenegado, setAccesoDenegado] = useState(null); // null o mensaje del backend
    const [serviciosExpandidos, setServiciosExpandidos] = useState(new Set());
    const [copiadoLink, setCopiadoLink] = useState(false);
    const [historial, setHistorial] = useState({ open: false, loading: false, items: [] });
    const [solicitudEnviada, setSolicitudEnviada] = useState(false);
    // Modal read-only para ver detalle de variaciones aplicadas a una fila/bloque/servicio
    const [variacionesReadModal, setVariacionesReadModal] = useState({ open: false });
    // Historial de versiones del hilo — se carga solo si esta cotizacion tiene
    // versiones (versiones_count > 1) Y no esta en borrador. El modal completo
    // se abre desde el boton "Ver todas".
    const [hiloItems, setHiloItems] = useState([]);
    const [hiloModal, setHiloModal] = useState(false);

    // Lock en modo viewer: solo polling para mostrar quien edita (si alguien edita)
    const { lock: lockInfo, solicitar: solicitarLock, forzar: forzarLock } = useCotizacionLock(id, {
        modo: 'viewer',
        enabled: true
    });

    // Tomar control forzado del lock (destructivo — el editor original pierde
    // cambios sin guardar). Requiere permiso 'forzar_lock_cotizacion'.
    // Confirmacion previa obligatoria.
    const handleForzarLock = async () => {
        const ownerName = lockInfo?.usuario_nombre || 'el editor actual';
        const ok = await confirm({
            title: 'Tomar control de la cotización',
            message: `${ownerName} está editando esta cotización. Si continúas, perderá los cambios sin guardar. Esta acción queda registrada en auditoría.`,
            confirmText: 'Tomar control',
            cancelText: 'Cancelar',
            danger: true
        });
        if (!ok) return;
        const res = await forzarLock();
        if (res?.ok) {
            toast.success(`Control tomado. ${res.desplazado ? `Desplazaste a ${res.desplazado.usuario_nombre}` : 'Ya no había editor activo'}`);
            navigate(`/cotizaciones/${id}`);
        } else {
            toast.error('No se pudo tomar el control');
        }
    };

    // Detectar el resultado de mi solicitud (rechazada vs aceptada).
    // - Si lockInfo es null: el owner libero el lock → aceptada, redirigir al form.
    // - Si lockInfo sigue pero solicitud no soy yo: el owner la rechazo, toast.
    useEffect(() => {
        if (!solicitudEnviada) return;
        if (!lockInfo) {
            // Lock liberado → aceptada. Aviso breve + redirect al form.
            toast.success('El editor liberó el control. Abriendo el formulario…', { duration: 2500 });
            setSolicitudEnviada(false);
            // Pequeno delay para que se vea el toast antes de navegar
            setTimeout(() => navigate(`/cotizaciones/${id}`), 400);
            return;
        }
        const miSolicitudActiva = lockInfo.solicitud?.usuario_id === user?.id;
        if (!miSolicitudActiva) {
            // El owner rechazo mi solicitud (limpio solicitud_de_*)
            toast(
                'Tu solicitud de edición fue rechazada. Puedes volver a intentar más tarde.',
                { icon: '🔒', duration: 5000 }
            );
            setSolicitudEnviada(false);
        }
    }, [lockInfo, solicitudEnviada, user?.id, id, navigate]);

    useDocumentTitle(
        cotizacion
            ? `${cotizacion.numero}${cotizacion.cliente_nombre ? ` · ${cotizacion.cliente_nombre}` : ''}`
            : 'Cotización'
    );

    // Cargar cotizacion — extraido del useEffect para poder refrescar desde
    // sub-componentes (ej. PagosCard cuando registra/anula un pago).
    const cargarCotizacion = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const res = await api.cotizaciones.getById(id);
            setCotizacion(res.data);
            if (!silent && res.data.servicios?.length > 0) {
                // Expandir el primer servicio NO opcional. Si todos fueran opcionales,
                // caer al primero del array. Evita que el bloque "Opcionales" (que se
                // renderiza al final) aparezca abierto por defecto.
                const primero = res.data.servicios.find(s => !s.es_opcional) || res.data.servicios[0];
                setServiciosExpandidos(new Set([primero.id]));
            }
        } catch (err) {
            const msg = err?.message || '';
            const status = err?.status;
            if (status === 403 || msg.includes('No tienes acceso') || msg.includes('acceso')) {
                setAccesoDenegado(msg || 'No tienes permiso para ver esta cotización.');
            } else if (status === 404 || msg.toLowerCase().includes('no encontrada') || msg.toLowerCase().includes('not found')) {
                // ID invalido / cotizacion inexistente → 404 fullscreen
                navigate('/404', { replace: true });
            } else {
                toast.error('No pudimos cargar la cotización');
                navigate('/cotizaciones');
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        cargarCotizacion();
    }, [cargarCotizacion]);

    // Cargar hilo de versiones SIEMPRE (sin importar estado ni versiones_count).
    // Antes se saltaba si versiones_count <= 1, pero eso hacia que al navegar
    // entre versiones el card apareciera y desapareciera segun el snapshot
    // vieja/nueva del `cotizacion`. Ahora pedimos siempre — es 1 endpoint chico
    // y garantiza consistencia. El render decide si mostrar o no segun hiloItems.
    const cargarHilo = useCallback(async () => {
        if (!id) return;
        try {
            const res = await api.cotizaciones.getHilo(id);
            setHiloItems(res.data?.items || []);
        } catch {
            setHiloItems([]);
        }
    }, [id]);

    useEffect(() => {
        cargarHilo();
    }, [cargarHilo]);

    // Comportamiento como en CotizacionForm: solo uno expandido a la vez.
    // Click en el mismo lo cierra, click en otro abre el nuevo y cierra el actual.
    const toggleServicio = (sid) => {
        setServiciosExpandidos(prev => {
            if (prev.has(sid)) return new Set();
            return new Set([sid]);
        });
    };

    const handleCopiarLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopiadoLink(true);
            setTimeout(() => setCopiadoLink(false), 2000);
        } catch {
            toast.error('No pudimos copiar el link');
        }
    };

    const handleDescargarPdf = async (preview = false) => {
        try {
            // Si la cotizacion tiene archivo adjunto (manual o normal-con-switch),
            // ese archivo REEMPLAZA el PDF generado por el sistema.
            if (cotizacion?.archivo_path) {
                if (preview) {
                    await api.cotizaciones.verArchivo(cotizacion.id);
                } else {
                    await api.cotizaciones.descargarArchivo(cotizacion.id);
                }
                return;
            }
            const tiposRes = await getCachedTiposServicio();
            // Recalcular subtotales al vuelo (excluyendo opcionales). Cotizaciones
            // guardadas antes del fix del backend pueden tener cotizacion.subtotal
            // con opcionales sumados; recalcular aca evita imprimir esos valores.
            const igvRateLocal = parseFloat(cotizacion.igv_rate) || parseFloat(configuracion?.igv_rate) || 0.18;
            const aplicaIgvLocal = cotizacion.incluye_igv !== false && cotizacion.incluye_igv !== 0;

            const subtotalServiciosLocal = (cotizacion.servicios || [])
                .filter(s => !s.es_opcional)
                .reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
            const subtotalExtrasLocal = (cotizacion.extras || [])
                .reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);
            const subtotalLocal = subtotalServiciosLocal + subtotalExtrasLocal;
            const igvLocal = aplicaIgvLocal ? subtotalLocal * igvRateLocal : 0;
            const totalLocal = subtotalLocal + igvLocal;

            const subtotalOpcLocal = (cotizacion.servicios || [])
                .filter(s => !!s.es_opcional)
                .reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
            const igvOpcLocal = aplicaIgvLocal ? subtotalOpcLocal * igvRateLocal : 0;
            const totalOpcLocal = subtotalOpcLocal + igvOpcLocal;

            await generatePdfCotizacion({
                cotizacion: {
                    ...cotizacion,
                    subtotal: subtotalLocal,
                    subtotalExtras: subtotalExtrasLocal,
                    igv: igvLocal,
                    total: totalLocal,
                    subtotalOpcionales: subtotalOpcLocal,
                    igvOpcionales: igvOpcLocal,
                    totalOpcionales: totalOpcLocal
                },
                servicios: cotizacion.servicios || [],
                extras: cotizacion.extras || [],
                configuracion: configuracion || {},
                tiposServicio: tiposRes.data || [],
                preview
            });
        } catch (err) {
            toast.error('Error al generar PDF: ' + err.message);
        }
    };

    const abrirHistorial = async () => {
        setHistorial({ open: true, loading: true, items: [] });
        try {
            const res = await api.cotizaciones.getEstadoHistorial(id);
            setHistorial({ open: true, loading: false, items: res.data || [] });
        } catch {
            toast.error('No pudimos cargar el historial');
            setHistorial({ open: false, loading: false, items: [] });
        }
    };

    // Crear una nueva version del hilo (mismo flow que el listado). Requiere
    // confirmacion y solo tiene sentido si la cotizacion actual no esta en
    // borrador — para borrador se sigue editando la misma cotizacion.
    const handleCrearVersion = async () => {
        const ok = await confirm({
            title: '¿Crear nueva versión?',
            message: 'Se generará una copia editable con nuevo correlativo (ej. -A, -B) que hereda los datos actuales. Podrás modificarla sin afectar la versión actual.',
            confirmText: 'Crear versión',
            cancelText: 'Cancelar'
        });
        if (!ok) return;
        try {
            const response = await api.cotizaciones.createVersion(id);
            toast.success(`Nueva versión creada: ${response.data.numero}`);
            navigate(`/cotizaciones/${response.data.id}`);
        } catch (err) {
            toast.error('Error al crear versión: ' + err.message);
        }
    };

    if (loading) {
        return <div className="loading">Cargando cotización...</div>;
    }

    if (accesoDenegado) {
        return <AccesoDenegado mensaje={accesoDenegado} />;
    }

    if (!cotizacion) return null;

    const moneda = cotizacion.moneda || 'PEN';
    const esBorrador = cotizacion.estado === 'borrador';
    const igvRate = parseFloat(cotizacion.igv_rate) || parseFloat(configuracion?.igv_rate) || 0.18;

    // Calcular costo por hora visible si no viene del backend
    const calcularSubtotal = (asig) => {
        const horas = (parseFloat(asig.horas) || 0) * 1.12;
        return horas * (parseFloat(asig.costo_hora) || 0);
    };

    return (
        <div className="cotizacion-detalle">
            {/* Banner de estado: dot + label sutil (sin barra lateral pesada) */}
            <div className="detalle-estado-banner">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span
                        className="estado-banner-dot"
                        style={{ background: getEstadoColor(cotizacion.estado) }}
                        aria-hidden="true"
                    />
                    <span
                        className="estado-banner-label"
                        style={{ color: getEstadoColor(cotizacion.estado) }}
                    >
                        {ESTADO_LABELS[cotizacion.estado]}
                    </span>
                    {cotizacion.updated_at && (
                        <span className="text-[11px] text-muted truncate">
                            ·{' '}
                            {cotizacion.actualizado_por_nombre
                                ? `Actualizada por ${cotizacion.actualizado_por_nombre} · ${formatDateTimeShort(cotizacion.updated_at)}`
                                : formatDateTimeShort(cotizacion.updated_at)}
                        </span>
                    )}
                </div>
                {!esBorrador && (
                    <span className="detalle-bloqueada-pill">
                        <Lock size={12} />
                        Cotización bloqueada — solo lectura
                    </span>
                )}
            </div>

            {/* Banner: alguien esta editando ahora mismo */}
            {lockInfo && !lockInfo.es_mio && esBorrador && (
                <div className="detalle-lock-banner" role="status">
                    <div className="detalle-lock-icon">
                        <UserCog size={18} aria-hidden="true" />
                    </div>
                    <div className="detalle-lock-body">
                        <div className="detalle-lock-titulo">
                            {lockInfo.usuario_nombre} está editando esta cotización
                        </div>
                        <div className="detalle-lock-msg">
                            No puedes editar mientras otro usuario tenga el control.
                            Puedes solicitarle que te lo ceda con el botón <strong>Solicitar edición</strong>.
                        </div>
                    </div>
                </div>
            )}

            {/* Header con número, datos cliente y acciones */}
            <Card className="mt-3">
                <Card.Header
                    title={cotizacion.numero}
                    subtitle={`Cotización para ${cotizacion.cliente_nombre}`}
                    actions={
                        <div className="flex items-center gap-2">
                            <Tooltip content="Volver al listado">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    leftIcon={<ArrowLeft size={14} />}
                                    onClick={() => navigate(-1)}
                                >
                                    Volver
                                </Button>
                            </Tooltip>
                            <Tooltip content="Ver historial de cambios de estado">
                                <Button
                                    variant="soft"
                                    size="sm"
                                    leftIcon={<History size={14} />}
                                    onClick={abrirHistorial}
                                >
                                    Historial
                                </Button>
                            </Tooltip>
                            <Tooltip content={copiadoLink ? 'Link copiado al portapapeles' : 'Copiar link directo de esta cotización'}>
                                <Button
                                    variant={copiadoLink ? 'primary' : 'soft'}
                                    size="sm"
                                    leftIcon={copiadoLink ? <Check size={14} /> : <Link2 size={14} />}
                                    onClick={handleCopiarLink}
                                >
                                    {copiadoLink ? '¡Copiado!' : 'Copiar link'}
                                </Button>
                            </Tooltip>
                            {verPdf && (
                                <>
                                    <Tooltip content="Abrir el PDF en una nueva pestaña">
                                        <Button
                                            variant="soft"
                                            size="sm"
                                            leftIcon={<FileText size={14} />}
                                            onClick={() => handleDescargarPdf(true)}
                                        >
                                            Ver PDF
                                        </Button>
                                    </Tooltip>
                                    <Tooltip content="Descargar el PDF al equipo">
                                        <Button
                                            variant="soft"
                                            size="sm"
                                            leftIcon={<Download size={14} />}
                                            onClick={() => handleDescargarPdf(false)}
                                        >
                                            Descargar PDF
                                        </Button>
                                    </Tooltip>
                                </>
                            )}
                            {esBorrador && puedeEditar && !lockInfo && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    leftIcon={<Pencil size={14} />}
                                    onClick={() => navigate(`/cotizaciones/${id}`)}
                                >
                                    Editar
                                </Button>
                            )}
                            {esBorrador && puedeEditar && lockInfo && !lockInfo.es_mio && (
                                <Button
                                    variant="soft"
                                    size="sm"
                                    leftIcon={<UserCog size={14} />}
                                    onClick={async () => {
                                        const res = await solicitarLock();
                                        if (res?.ok) {
                                            setSolicitudEnviada(true);
                                            toast.success(`Solicitud enviada a ${lockInfo.usuario_nombre}`);
                                        }
                                    }}
                                    disabled={solicitudEnviada}
                                >
                                    {solicitudEnviada ? 'Solicitud enviada' : 'Solicitar edición'}
                                </Button>
                            )}
                            {esBorrador && puedeEditar && lockInfo && !lockInfo.es_mio && puedeForzarLock && (
                                <Tooltip content={`${lockInfo.usuario_nombre} pierde sus cambios sin guardar.`}>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        leftIcon={<Zap size={14} />}
                                        onClick={handleForzarLock}
                                    >
                                        Tomar control
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    }
                />
                <Card.Body>
                    <div className="detalle-info-grid">
                        <div className="detalle-info-block">
                            <span className="detalle-info-label">Cliente</span>
                            <span className="detalle-info-value">{cotizacion.cliente_nombre}</span>
                            {cotizacion.cliente_empresa && (
                                <span className="detalle-info-sub">{cotizacion.cliente_empresa}</span>
                            )}
                        </div>
                        {cotizacion.cliente_email && (
                            <div className="detalle-info-block">
                                <span className="detalle-info-label">Email</span>
                                <span className="detalle-info-value">{cotizacion.cliente_email}</span>
                            </div>
                        )}
                        {cotizacion.cliente_telefono && (
                            <div className="detalle-info-block">
                                <span className="detalle-info-label">Teléfono</span>
                                <span className="detalle-info-value">{cotizacion.cliente_telefono}</span>
                            </div>
                        )}
                        <div className="detalle-info-block">
                            <span className="detalle-info-label">Fecha</span>
                            <span className="detalle-info-value">{formatearFecha(cotizacion.fecha)}</span>
                        </div>
                        <div className="detalle-info-block">
                            <span className="detalle-info-label">Validez</span>
                            <span className="detalle-info-value">{cotizacion.validez || 15} días</span>
                        </div>
                        <div className="detalle-info-block">
                            <span className="detalle-info-label">Moneda</span>
                            <span className="detalle-info-value">
                                {moneda === 'USD' ? 'Dólares (US$)' : 'Soles (S/)'}
                            </span>
                        </div>
                        {cotizacion.tipo_cambio && (
                            <div className="detalle-info-block">
                                <span className="detalle-info-label">Tipo de cambio</span>
                                <span className="detalle-info-value">
                                    1 USD = S/ {parseFloat(cotizacion.tipo_cambio).toFixed(4)}
                                </span>
                            </div>
                        )}
                        {cotizacion.tiempo_desarrollo && (
                            <div className="detalle-info-block">
                                <span className="detalle-info-label">Tiempo de desarrollo</span>
                                <span className="detalle-info-value">{cotizacion.tiempo_desarrollo}</span>
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* Archivo adjunto: aplica a cotizaciones manuales Y a normales con
                switch de archivo adjunto (archivo_path presente).
                Se comprueba con string() para evitar renderizar "0" cuando
                MySQL devuelve un valor no-string por alguna razon. */}
            {typeof cotizacion.archivo_path === 'string' && cotizacion.archivo_path.length > 0 && (
                <Card className="mt-3">
                    <Card.Header title="Archivo adjunto" />
                    <Card.Body>
                        <div className="archivo-detalle">
                            <div className="archivo-detalle-info">
                                <strong>{cotizacion.archivo_nombre || 'archivo'}</strong>
                                <span className="text-xs text-muted">Cotización manual</span>
                            </div>
                            <div className="archivo-detalle-acciones">
                                <Button
                                    variant="soft"
                                    size="sm"
                                    onClick={async () => {
                                        try { await api.cotizaciones.verArchivo(cotizacion.id); }
                                        catch (e) { toast.error(e.message); }
                                    }}
                                >
                                    Ver
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={async () => {
                                        try { await api.cotizaciones.descargarArchivo(cotizacion.id); }
                                        catch (e) { toast.error(e.message); }
                                    }}
                                >
                                    Descargar
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Servicios con accordion — se omite en cotizaciones manuales */}
            {!cotizacion.es_manual && (
            <Card className="mt-3">
                <Card.Header title="Servicios" />
                <Card.Body>
                    {(cotizacion.servicios || []).length === 0 ? (
                        <p className="text-sm text-muted">Esta cotización no tiene servicios.</p>
                    ) : (
                        <div className="servicios-list">
                            {(() => {
                                // Renderizar normales primero, luego separador y opcionales.
                                return [
                                    ...(cotizacion.servicios || []).filter(s => !s.es_opcional),
                                    ...(cotizacion.servicios || []).filter(s => !!s.es_opcional)
                                ];
                            })().map((s, idx, arr) => {
                                const expanded = serviciosExpandidos.has(s.id);
                                const esOpcional = !!s.es_opcional;
                                const esPrimeraOpcional = esOpcional && arr.findIndex(x => !!x.es_opcional) === idx;
                                return (
                                    <React.Fragment key={s.id}>
                                    {esPrimeraOpcional && (
                                        <div className="costos-divider opcionales-divider">
                                            <h3 className="costos-divider-title">
                                                <Info size={16} aria-hidden="true" />
                                                <span>Opcionales</span>
                                            </h3>
                                            <span className="text-xs text-muted">
                                                Servicios opcionales — no se suman al total.
                                            </span>
                                        </div>
                                    )}
                                    <div
                                        className={`servicio-item ${expanded ? 'expanded' : 'collapsed'} ${esOpcional ? 'is-opcional' : ''}`}
                                    >
                                        <div
                                            className="servicio-header"
                                            onClick={() => toggleServicio(s.id)}
                                        >
                                            <div className="servicio-header-left">
                                                <span className="servicio-number">#{idx + 1}</span>
                                                <span className="servicio-nombre-display">
                                                    {s.tipo_servicio_nombre || 'Servicio'}
                                                </span>
                                                {esOpcional && (
                                                    <span className="servicio-opcional-badge" title="Servicio opcional — no suma al total">Opcional</span>
                                                )}
                                            </div>
                                            <div className="servicio-header-right">
                                                {(() => {
                                                    // Si hay oferta, el s.total persistido ya viene con la oferta aplicada.
                                                    // Reconstruimos el precio "antes de oferta" desde la cadena de variaciones
                                                    // para mostrar el tachado. Este calculo es solo cosmetico.
                                                    const hayOfertaServ = !!s.oferta;
                                                    let totalAntesOferta = null;
                                                    if (hayOfertaServ) {
                                                        let tfp = 0;
                                                        (s.asignaciones || []).forEach(a => {
                                                            const sub = a.subtotal != null ? parseFloat(a.subtotal) : 0;
                                                            const rent = a.rentabilidad != null ? parseFloat(a.rentabilidad) : 0;
                                                            const tPre = rowTotalConVariaciones(sub, rent, a.total_override, a.variaciones);
                                                            tfp += aplicarOferta(tPre, a.oferta);
                                                        });
                                                        const tbp = aplicarVariaciones(tfp, s.variaciones_bloque_personal || []);
                                                        let tfc = 0;
                                                        (s.costosFijos || []).forEach(cf => {
                                                            const tCf = rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                                                            tfc += aplicarOferta(tCf, cf.oferta);
                                                        });
                                                        const tbc = aplicarVariaciones(tfc, s.variaciones_bloque_costos || []);
                                                        totalAntesOferta = aplicarVariaciones(tbp + tbc, s.variaciones || []);
                                                    }
                                                    return (
                                                        <span className="servicio-total">
                                                            {hayOfertaServ && (
                                                                <span className="oferta-precio-tachado" title="Precio original">
                                                                    {blurMoney(totalAntesOferta, moneda)}
                                                                </span>
                                                            )}
                                                            <span className={hayOfertaServ ? 'oferta-precio-final' : ''}>
                                                                Total: {blurMoney(s.total, moneda)}
                                                            </span>
                                                            {s.periodo_nombre && (
                                                                <span className="servicio-total-periodo"> / {String(s.periodo_nombre).toLowerCase()}</span>
                                                            )}
                                                            {hayOfertaServ && (
                                                                <span className="oferta-badge-detalle" title={s.oferta.motivo}>
                                                                    <Tag size={11} aria-hidden="true" />
                                                                    {s.oferta.motivo}
                                                                </span>
                                                            )}
                                                        </span>
                                                    );
                                                })()}
                                                {/* Badge de variaciones a nivel servicio (Fase 2). Al click abre
                                                    modal read-only con la lista. Base = suma bloques con variaciones
                                                    de fila+bloque aplicadas. */}
                                                {Array.isArray(s.variaciones) && s.variaciones.length > 0 && (() => {
                                                    let totFilasPers = 0;
                                                    (s.asignaciones || []).forEach(a => {
                                                        const sub = a.subtotal != null ? parseFloat(a.subtotal) : 0;
                                                        const rent = a.rentabilidad != null ? parseFloat(a.rentabilidad) : 0;
                                                        totFilasPers += rowTotalConVariaciones(sub, rent, a.total_override, a.variaciones);
                                                    });
                                                    const totBloquePers = aplicarVariaciones(totFilasPers, s.variaciones_bloque_personal || []);
                                                    let totFilasCf = 0;
                                                    (s.costosFijos || []).forEach(cf => {
                                                        totFilasCf += rowTotalConVariaciones(cf.subtotal, 0, cf.total_override, cf.variaciones);
                                                    });
                                                    const totBloqueCf = aplicarVariaciones(totFilasCf, s.variaciones_bloque_costos || []);
                                                    const baseServ = totBloquePers + totBloqueCf;
                                                    return (
                                                        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                            <VariacionesReadOnly
                                                                variaciones={s.variaciones}
                                                                onOpen={() => setVariacionesReadModal({
                                                                    open: true,
                                                                    base: baseServ,
                                                                    variaciones: s.variaciones,
                                                                    titulo: `Variaciones — ${s.tipo_servicio_nombre || 'Servicio'}`
                                                                })}
                                                            />
                                                        </span>
                                                    );
                                                })()}
                                                <button
                                                    className={`toggle-icon ${expanded ? 'rotated' : ''}`}
                                                    type="button"
                                                    aria-expanded={expanded}
                                                    aria-controls={`servicio-content-${s.id}`}
                                                    aria-label={expanded ? 'Colapsar servicio' : 'Expandir servicio'}
                                                    onClick={(e) => { e.stopPropagation(); toggleServicio(s.id); }}
                                                >
                                                    <ChevronDown size={20} aria-hidden="true" />
                                                </button>
                                            </div>
                                        </div>

                                        <div
                                            id={`servicio-content-${s.id}`}
                                            className={`servicio-content ${expanded ? 'open' : ''}`}
                                            role="region"
                                            aria-label={`Detalle del servicio ${idx + 1}`}
                                        >
                                            <div className="servicio-content-inner">
                                                {/* Titulo del servicio — encabezado que engloba el detalle rico */}
                                                {(s.titulo || s.tipo_servicio_nombre) && (
                                                    <h4 className="detalle-servicio-titulo">
                                                        {s.titulo || s.tipo_servicio_nombre}
                                                    </h4>
                                                )}
                                                {s.detalle && (
                                                    <div
                                                        className="detalle-html"
                                                        dangerouslySetInnerHTML={{ __html: s.detalle }}
                                                    />
                                                )}

                                                {verDetalles && s.asignaciones?.length > 0 && (
                                                    <div className="detalle-subsection detalle-subsection-personal">
                                                        <h4 className="detalle-subsection-title">Personal asignado</h4>
                                                        <div className="detalle-tablewrap">
                                                            <table className="detalle-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Personal</th>
                                                                        <th style={{ textAlign: 'right' }}>Horas</th>
                                                                        <th style={{ textAlign: 'right' }}>Costo/hr</th>
                                                                        <th style={{ textAlign: 'right' }}>Subtotal</th>
                                                                        <th style={{ textAlign: 'right' }}>Ganancia</th>
                                                                        <th style={{ textAlign: 'right' }}>Total</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {s.asignaciones.map((a, i) => {
                                                                        const subtotalAsig = a.subtotal != null ? parseFloat(a.subtotal) : calcularSubtotal(a);
                                                                        const rentAsig = a.rentabilidad != null ? parseFloat(a.rentabilidad) : 0;
                                                                        const totalAsigPreOferta = rowTotalConVariaciones(subtotalAsig, rentAsig, a.total_override, a.variaciones);
                                                                        // Oferta comercial visible al cliente
                                                                        const hayOfertaAsig = !!a.oferta;
                                                                        const totalAsig = aplicarOferta(totalAsigPreOferta, a.oferta);
                                                                        const gananciaAsig = totalAsig - subtotalAsig;
                                                                        const overridden = hasOverride(a.total_override) || (Array.isArray(a.variaciones) && a.variaciones.length > 0);
                                                                        return (
                                                                            <tr key={i}>
                                                                                <td>
                                                                                    <div>{a.personal_nombre || '—'}</div>
                                                                                    {a.descripcion && (
                                                                                        <div className="detalle-fila-descripcion">{a.descripcion}</div>
                                                                                    )}
                                                                                </td>
                                                                                <td style={{ textAlign: 'right' }}>{parseFloat(a.horas) || 0}</td>
                                                                                <td style={{ textAlign: 'right' }}>{formatCurrency(a.costo_hora, moneda)}</td>
                                                                                <td style={{ textAlign: 'right' }}>{formatCurrency(subtotalAsig, moneda)}</td>
                                                                                <td style={{ textAlign: 'right', color: gananciaAsig >= 0 ? 'rgb(var(--success))' : 'rgb(var(--danger))' }}>
                                                                                    {formatCurrency(gananciaAsig, moneda)}
                                                                                </td>
                                                                                <td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }} title={hasOverride(a.total_override) ? `Ajuste manual heredado. Calculado: ${blurMoney(totalConRentabilidad(subtotalAsig, rentAsig), moneda)}` : undefined}>
                                                                                    {hayOfertaAsig && (
                                                                                        <span className="oferta-precio-tachado" title="Precio original">
                                                                                            {blurMoney(totalAsigPreOferta, moneda)}
                                                                                        </span>
                                                                                    )}
                                                                                    <span className={hayOfertaAsig ? 'oferta-precio-final' : ''}>
                                                                                        {blurMoney(totalAsig, moneda)}
                                                                                    </span>
                                                                                    {hasOverride(a.total_override) && !Array.isArray(a.variaciones)?.length && (
                                                                                        <span style={{ marginLeft: 4, color: 'rgb(var(--warning))', fontSize: 11 }}>•</span>
                                                                                    )}
                                                                                    <VariacionesReadOnly
                                                                                        variaciones={a.variaciones}
                                                                                        onOpen={() => setVariacionesReadModal({
                                                                                            open: true,
                                                                                            base: subtotalAsig != null ? rowTotal(subtotalAsig, rentAsig, a.total_override) : 0,
                                                                                            variaciones: a.variaciones || [],
                                                                                            titulo: `Variaciones — ${a.personal_nombre || 'Personal'}`
                                                                                        })}
                                                                                    />
                                                                                    {hayOfertaAsig && (
                                                                                        <div className="oferta-motivo-inline" title={a.oferta.motivo}>
                                                                                            <Tag size={10} aria-hidden="true" />
                                                                                            <span className="oferta-motivo-texto">{a.oferta.motivo}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                                <tfoot>
                                                                    {(() => {
                                                                        // Suma subtotal/ganancia/total sobre todas las filas de personal
                                                                        // del servicio. Aplica oferta comercial por fila si existe.
                                                                        const sumsFilas = s.asignaciones.reduce((acc, a) => {
                                                                            const sub = a.subtotal != null ? parseFloat(a.subtotal) : calcularSubtotal(a);
                                                                            const rent = a.rentabilidad != null ? parseFloat(a.rentabilidad) : 0;
                                                                            const totPre = rowTotalConVariaciones(sub, rent, a.total_override, a.variaciones);
                                                                            const tot = aplicarOferta(totPre, a.oferta);
                                                                            const gan = tot - sub;
                                                                            return { sub: acc.sub + sub, gan: acc.gan + gan, tot: acc.tot + tot };
                                                                        }, { sub: 0, gan: 0, tot: 0 });
                                                                        // Aplicar variaciones a nivel bloque_personal (Fase 2)
                                                                        const totConBloque = aplicarVariaciones(sumsFilas.tot, s.variaciones_bloque_personal || []);
                                                                        const sums = {
                                                                            sub: sumsFilas.sub,
                                                                            tot: totConBloque,
                                                                            gan: totConBloque - sumsFilas.sub
                                                                        };
                                                                        return (
                                                                            <tr className="detalle-table-total">
                                                                                <td colSpan={3} style={{ fontWeight: 600 }}>TOTAL</td>
                                                                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(sums.sub, moneda)}</td>
                                                                                <td style={{ textAlign: 'right', fontWeight: 600, color: sums.gan >= 0 ? 'rgb(var(--success))' : 'rgb(var(--danger))' }}>{formatCurrency(sums.gan, moneda)}</td>
                                                                                <td style={{ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                                    {formatCurrency(sums.tot, moneda)}
                                                                                    <VariacionesReadOnly
                                                                                        variaciones={s.variaciones_bloque_personal}
                                                                                        onOpen={() => setVariacionesReadModal({
                                                                                            open: true,
                                                                                            base: sumsFilas.tot,
                                                                                            variaciones: s.variaciones_bloque_personal || [],
                                                                                            titulo: 'Variaciones — Total Personal'
                                                                                        })}
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })()}
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {verDetalles && s.costosFijos?.length > 0 && (
                                                    <div className="detalle-subsection detalle-subsection-costos">
                                                        <h4 className="detalle-subsection-title">Costos fijos</h4>
                                                        <div className="detalle-tablewrap">
                                                            <table className="detalle-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Concepto</th>
                                                                        <th>Categoría</th>
                                                                        <th style={{ textAlign: 'right' }}>Costo</th>
                                                                        <th style={{ textAlign: 'right' }}>Ganancia</th>
                                                                        <th style={{ textAlign: 'right' }}>Total</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {s.costosFijos.map((cf, i) => {
                                                                        const costoCf = parseFloat(cf.costo) || 0;
                                                                        const rentCf = cf.rentabilidad != null ? parseFloat(cf.rentabilidad) : 0;
                                                                        const subtotalCf = parseFloat(cf.subtotal) || totalConRentabilidad(costoCf, rentCf);
                                                                        const totalCfPreOferta = rowTotalConVariaciones(subtotalCf, 0, cf.total_override, cf.variaciones);
                                                                        // Oferta comercial visible al cliente
                                                                        const hayOfertaCf = !!cf.oferta;
                                                                        const totalCf = aplicarOferta(totalCfPreOferta, cf.oferta);
                                                                        const gananciaCf = totalCf - costoCf;
                                                                        const overridden = hasOverride(cf.total_override) || (Array.isArray(cf.variaciones) && cf.variaciones.length > 0);
                                                                        return (
                                                                            <tr key={i}>
                                                                                <td>
                                                                                    <div>{cf.costo_fijo_nombre || '—'}</div>
                                                                                    {cf.descripcion && (
                                                                                        <div className="detalle-fila-descripcion">{cf.descripcion}</div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="text-muted">{cf.categoria_nombre || '—'}</td>
                                                                                <td style={{ textAlign: 'right' }}>{formatCurrency(costoCf, moneda)}</td>
                                                                                <td style={{ textAlign: 'right', color: gananciaCf >= 0 ? 'rgb(var(--success))' : 'rgb(var(--danger))' }}>
                                                                                    {formatCurrency(gananciaCf, moneda)}
                                                                                </td>
                                                                                <td style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }} title={hasOverride(cf.total_override) ? `Ajuste manual heredado. Calculado: ${blurMoney(subtotalCf, moneda)}` : undefined}>
                                                                                    {hayOfertaCf && (
                                                                                        <span className="oferta-precio-tachado" title="Precio original">
                                                                                            {blurMoney(totalCfPreOferta, moneda)}
                                                                                        </span>
                                                                                    )}
                                                                                    <span className={hayOfertaCf ? 'oferta-precio-final' : ''}>
                                                                                        {blurMoney(totalCf, moneda)}
                                                                                    </span>
                                                                                    {cf.periodo_nombre && (
                                                                                        <span className="text-xs text-muted" style={{ marginLeft: 4, fontWeight: 400 }}>
                                                                                            / {String(cf.periodo_nombre).toLowerCase()}
                                                                                        </span>
                                                                                    )}
                                                                                    {hasOverride(cf.total_override) && !Array.isArray(cf.variaciones)?.length && (
                                                                                        <span style={{ marginLeft: 4, color: 'rgb(var(--warning))', fontSize: 11 }}>•</span>
                                                                                    )}
                                                                                    <VariacionesReadOnly
                                                                                        variaciones={cf.variaciones}
                                                                                        onOpen={() => setVariacionesReadModal({
                                                                                            open: true,
                                                                                            base: rowTotal(subtotalCf, 0, cf.total_override),
                                                                                            variaciones: cf.variaciones || [],
                                                                                            titulo: `Variaciones — ${cf.costo_fijo_nombre || 'Costo fijo'}`
                                                                                        })}
                                                                                    />
                                                                                    {hayOfertaCf && (
                                                                                        <div className="oferta-motivo-inline" title={cf.oferta.motivo}>
                                                                                            <Tag size={10} aria-hidden="true" />
                                                                                            <span className="oferta-motivo-texto">{cf.oferta.motivo}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                                <tfoot>
                                                                    {(() => {
                                                                        const sumsFilas = s.costosFijos.reduce((acc, cf) => {
                                                                            const costo = parseFloat(cf.costo) || 0;
                                                                            const rent = cf.rentabilidad != null ? parseFloat(cf.rentabilidad) : 0;
                                                                            const sub = parseFloat(cf.subtotal) || totalConRentabilidad(costo, rent);
                                                                            const totPre = rowTotalConVariaciones(sub, 0, cf.total_override, cf.variaciones);
                                                                            // La suma respeta la oferta comercial de cada CF
                                                                            const tot = aplicarOferta(totPre, cf.oferta);
                                                                            const gan = tot - costo;
                                                                            return { costo: acc.costo + costo, gan: acc.gan + gan, tot: acc.tot + tot };
                                                                        }, { costo: 0, gan: 0, tot: 0 });
                                                                        // Aplicar variaciones a nivel bloque_costos (Fase 2)
                                                                        const totConBloque = aplicarVariaciones(sumsFilas.tot, s.variaciones_bloque_costos || []);
                                                                        const sums = {
                                                                            costo: sumsFilas.costo,
                                                                            tot: totConBloque,
                                                                            gan: totConBloque - sumsFilas.costo
                                                                        };
                                                                        return (
                                                                            <tr className="detalle-table-total">
                                                                                <td colSpan={2} style={{ fontWeight: 600 }}>TOTAL</td>
                                                                                <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(sums.costo, moneda)}</td>
                                                                                <td style={{ textAlign: 'right', fontWeight: 600, color: sums.gan >= 0 ? 'rgb(var(--success))' : 'rgb(var(--danger))' }}>{formatCurrency(sums.gan, moneda)}</td>
                                                                                <td style={{ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                                    {formatCurrency(sums.tot, moneda)}
                                                                                    <VariacionesReadOnly
                                                                                        variaciones={s.variaciones_bloque_costos}
                                                                                        onOpen={() => setVariacionesReadModal({
                                                                                            open: true,
                                                                                            base: sumsFilas.tot,
                                                                                            variaciones: s.variaciones_bloque_costos || [],
                                                                                            titulo: 'Variaciones — Total Costos Fijos'
                                                                                        })}
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })()}
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}
                </Card.Body>
            </Card>
            )}

            {/* Extras */}
            {cotizacion.extras?.length > 0 && (
                <Card className="mt-3">
                    <Card.Header title="Extras" />
                    <Card.Body>
                        <div className="detalle-tablewrap">
                            <table className="detalle-table">
                                <thead>
                                    <tr>
                                        <th>Descripción</th>
                                        <th style={{ textAlign: 'right' }}>Importe</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cotizacion.extras.map((e, i) => (
                                        <tr key={i}>
                                            <td>
                                                <div dangerouslySetInnerHTML={{ __html: e.detalle || 'Extra' }} />
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {formatCurrency(e.costo, moneda)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Totales — incluye ganancia total (suma de ganancias por item de cada servicio) */}
            <Card className="mt-3">
                <Card.Body>
                    {(() => {
                        // Ganancia total: aplicamos toda la cadena de variaciones
                        // (fila → bloque → servicio) y restamos el costo fisico.
                        // Ganancia = TotalConVariaciones − Costo(subtotales sin variar)
                        // Costo fisico total solo de los NO opcionales (para calcular ganancia).
                        let costoTotal = 0;
                        (cotizacion.servicios || []).forEach(s => {
                            if (s.es_opcional) return;
                            (s.asignaciones || []).forEach(a => {
                                costoTotal += a.subtotal != null ? parseFloat(a.subtotal) : 0;
                            });
                            (s.costosFijos || []).forEach(cf => {
                                costoTotal += parseFloat(cf.costo) || 0;
                            });
                        });
                        // Usamos s.total (persistido en BD) para el subtotal — es el valor
                        // autoritativo, mismo que el backend guarda y el form envia.
                        const totalConVar = (cotizacion.servicios || [])
                            .filter(s => !s.es_opcional)
                            .reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
                        const subtotalOpcionales = (cotizacion.servicios || [])
                            .filter(s => !!s.es_opcional)
                            .reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
                        const gananciaTotal = totalConVar - costoTotal;
                        const aplicaIgvDet = cotizacion.incluye_igv !== false && cotizacion.incluye_igv !== 0;
                        const totalOpcionales = subtotalOpcionales + (aplicaIgvDet ? subtotalOpcionales * igvRate : 0);
                        // Recalculamos en vivo (excluyendo opcionales) para no depender de
                        // cotizacion.subtotal/total guardados — cotizaciones anteriores al
                        // fix del backend podrian tener opcionales sumados en el total de BD.
                        const subtotalExtrasDet = (cotizacion.extras || []).reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);
                        const subtotalReal = totalConVar + subtotalExtrasDet;
                        const igvReal = aplicaIgvDet ? subtotalReal * igvRate : 0;
                        const totalReal = subtotalReal + igvReal;
                        return (
                            <div className="detalle-totales">
                                <div className="detalle-total-row">
                                    <span>Subtotal</span>
                                    <span>{blurMoney(subtotalReal, moneda)}</span>
                                </div>
                                {aplicaIgvDet && (
                                    <div className="detalle-total-row">
                                        <span>IGV ({Math.round(igvRate * 100)}%)</span>
                                        <span>{blurMoney(igvReal, moneda)}</span>
                                    </div>
                                )}
                                {cotizacion.descuento > 0 && (
                                    <div className="detalle-total-row">
                                        <span>Descuento</span>
                                        <span>-{blurMoney(cotizacion.descuento, moneda)}</span>
                                    </div>
                                )}
                                {verDetalles && gananciaTotal !== 0 && (
                                    <div className="detalle-total-row detalle-total-ganancia">
                                        <span>Ganancia</span>
                                        <span style={{ color: gananciaTotal >= 0 ? 'rgb(var(--success))' : 'rgb(var(--danger))' }}>
                                            {formatCurrency(gananciaTotal, moneda)}
                                        </span>
                                    </div>
                                )}
                                <div className="detalle-total-row detalle-total-final">
                                    <span>Total</span>
                                    <span>{blurMoney(totalReal, moneda)}</span>
                                </div>
                                {subtotalOpcionales > 0 && (
                                    <>
                                        <div className="detalle-total-row" style={{ marginTop: 8, borderTop: '1px dashed rgb(var(--border))', paddingTop: 8 }}>
                                            <span>Subtotal opcionales</span>
                                            <span>{blurMoney(subtotalOpcionales, moneda)}</span>
                                        </div>
                                        {aplicaIgvDet && (
                                            <div className="detalle-total-row">
                                                <span>IGV opcionales ({Math.round(igvRate * 100)}%)</span>
                                                <span>{blurMoney(subtotalOpcionales * igvRate, moneda)}</span>
                                            </div>
                                        )}
                                        <div className="detalle-total-row" style={{ fontWeight: 600 }}>
                                            <span title="Servicios opcionales — no incluidos en el total">Total opcionales</span>
                                            <span>{blurMoney(totalOpcionales, moneda)}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })()}
                </Card.Body>
            </Card>

            {/* Plan de pago (uso interno) — muestra las cuotas del plan seleccionado
                con el monto calculado sobre el total actual de la cotizacion. Aparece
                justo debajo de los totales y se hereda a las versiones (create_version
                copia plan_pago_id), asi cualquier version del hilo lo muestra tambien. */}
            {cotizacion.plan_pago && (cotizacion.plan_pago.periodos || []).length > 0 && (
                <Card className="mt-3">
                    <Card.Header
                        title={
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <CreditCard size={16} aria-hidden="true" />
                                Plan de pago
                                <span style={{
                                    fontSize: 11, fontWeight: 500, color: 'rgb(var(--muted))',
                                    background: 'rgb(var(--surface-2) / 0.6)',
                                    padding: '2px 8px', borderRadius: 999, marginLeft: 4
                                }}>
                                    Hasta {(cotizacion.plan_pago.periodos || []).length} cuota{(cotizacion.plan_pago.periodos || []).length === 1 ? '' : 's'}
                                </span>
                            </span>
                        }
                    />
                    <Card.Body>
                        <div className="plan-pago-detalle">
                            <div className="plan-pago-detalle-nombre">
                                <strong>{cotizacion.plan_pago.nombre}</strong>
                                {cotizacion.plan_pago.descripcion && (
                                    <span className="text-xs text-muted"> · {cotizacion.plan_pago.descripcion}</span>
                                )}
                            </div>
                            {calcularMontosPlanPago(cotizacion.total, cotizacion.plan_pago.periodos || []).map(per => (
                                <div key={per.id} className="plan-pago-desglose-row">
                                    <span className="plan-pago-desglose-num">#{per.numero_periodo}</span>
                                    <span className="plan-pago-desglose-nombre">{per.nombre}</span>
                                    <span className="plan-pago-desglose-pct">{per.porcentaje}%</span>
                                    <span className="plan-pago-desglose-monto">
                                        {verDetalles ? formatCurrency(per.monto, moneda) : '•••••'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Cobranzas — solo en cotizaciones aprobadas y con permiso gestion_cobranzas.
                Recargamos la cotizacion al cambiar para reflejar estado_pago en el badge. */}
            {cotizacion.estado === 'aprobada' && tienePermiso('gestion_cobranzas') && (
                <PagosCard
                    cotizacion={cotizacion}
                    onChange={() => cargarCotizacion({ silent: true })}
                />
            )}

            {/* Historial de versiones del hilo — solo visible cuando la cotizacion
                actual NO esta en borrador y hay >1 version en el hilo. Resumen
                de cada version (numero, estado, fecha, total) + boton "Ver todas"
                que abre el modal completo. */}
            {hiloItems.length > 1 && (
                <Card className="mt-3">
                    <Card.Header
                        title={
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <FileStack size={16} aria-hidden="true" />
                                Historial de versiones
                                <span style={{
                                    fontSize: 11, fontWeight: 500, color: 'rgb(var(--muted))',
                                    background: 'rgb(var(--surface-2) / 0.6)',
                                    padding: '2px 8px', borderRadius: 999, marginLeft: 4
                                }}>
                                    {hiloItems.length}
                                </span>
                            </span>
                        }
                        actions={
                            <div style={{ display: 'inline-flex', gap: 8 }}>
                                {cotizacion.estado !== 'borrador' && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        leftIcon={<Plus size={14} />}
                                        onClick={handleCrearVersion}
                                    >
                                        Nueva versión
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    leftIcon={<Eye size={14} />}
                                    onClick={() => setHiloModal(true)}
                                >
                                    Ver todas
                                </Button>
                            </div>
                        }
                    />
                    <Card.Body>
                        <div className="hilo-detalle-lista">
                            {hiloItems.map(item => {
                                const esActual = item.id === parseInt(id, 10);
                                const esPrincipal = !!item.es_version_principal;
                                return (
                                    <div
                                        key={item.id}
                                        className={`hilo-detalle-item ${esActual ? 'is-actual' : ''} ${esPrincipal ? 'is-principal' : ''}`}
                                    >
                                        <div className="hilo-detalle-info">
                                            <span className="hilo-detalle-numero">
                                                {item.numero}
                                                {esPrincipal && (
                                                    <span className="hilo-detalle-tag hilo-detalle-tag-principal">
                                                        <Star size={10} /> Principal
                                                    </span>
                                                )}
                                                {esActual && (
                                                    <span className="hilo-detalle-tag hilo-detalle-tag-actual">
                                                        Estás aquí
                                                    </span>
                                                )}
                                            </span>
                                            <span className="hilo-detalle-meta">
                                                <span style={{ color: getEstadoColor(item.estado) }}>
                                                    {ESTADO_LABELS[item.estado] || item.estado}
                                                </span>
                                                {' · '}
                                                {formatDateTimeShort(item.created_at)}
                                                {' · '}
                                                {item.creador_nombre || 'Sin autor'}
                                            </span>
                                        </div>
                                        <div className="hilo-detalle-right">
                                            <span className="hilo-detalle-total">
                                                {formatCurrency(item.total, item.moneda || moneda)}
                                            </span>
                                            {!esActual && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    leftIcon={<Eye size={12} />}
                                                    onClick={() => navigate(`/cotizaciones/${item.id}/detalle`)}
                                                >
                                                    Ver
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Prompt "Crear primera version" cuando no hay versiones en el hilo
                y la cotizacion no esta en borrador. Da acceso directo a la accion
                sin obligar al usuario a ir al listado. */}
            {hiloItems.length <= 1 && cotizacion.estado !== 'borrador' && (
                <Card className="mt-3">
                    <Card.Body>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <GitBranch size={22} style={{ color: 'rgb(var(--muted))', flexShrink: 0 }} />
                                <div>
                                    <div style={{ fontWeight: 600, marginBottom: 2 }}>Esta cotización aún no tiene versiones</div>
                                    <div style={{ fontSize: 13, color: 'rgb(var(--muted))' }}>
                                        Crea una nueva versión para editar los datos sin afectar la actual (se generará con sufijo -A, -B, etc).
                                    </div>
                                </div>
                            </div>
                            <Button
                                variant="primary"
                                size="sm"
                                leftIcon={<Plus size={14} />}
                                onClick={handleCrearVersion}
                            >
                                Nueva versión
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Comentarios */}
            {cotizacion.comentarios && (
                <Card className="mt-3">
                    <Card.Header title="Comentarios" />
                    <Card.Body>
                        <div
                            className="detalle-html"
                            dangerouslySetInnerHTML={{ __html: cotizacion.comentarios }}
                        />
                    </Card.Body>
                </Card>
            )}

            {/* Anexos globales del hilo — solo lectura en detalle (agregar/eliminar
                se hace desde el form en borrador). Se muestra siempre para que
                el usuario vea los archivos aunque la cotizacion este bloqueada. */}
            {/* Anexos: en cotizaciones manuales permitimos agregar/eliminar
                anexos aun en modo detalle, porque estas cotizaciones no tienen
                un "form en borrador" al que volver. Para las normales sigue
                siendo readonly (se editan desde el form en borrador). */}
            <AnexosSection cotizacionId={cotizacion.id} readonly={!cotizacion.es_manual} />

            {/* Modal historial de estados */}
            <Modal
                open={historial.open}
                onClose={() => setHistorial({ open: false, loading: false, items: [] })}
                title="Historial de estados"
                size="md"
            >
                {historial.loading ? (
                    <p className="text-sm text-muted">Cargando…</p>
                ) : historial.items.length === 0 ? (
                    <p className="text-sm text-muted">
                        Esta cotización aún no tiene cambios de estado registrados.
                    </p>
                ) : (
                    <ol className="estado-timeline">
                        {historial.items.map((h, idx) => (
                            <li key={h.id} className="estado-timeline-item">
                                <span
                                    className="estado-timeline-dot"
                                    style={{ background: getEstadoColor(h.estado_nuevo) }}
                                    aria-hidden="true"
                                />
                                {idx < historial.items.length - 1 && (
                                    <span className="estado-timeline-line" aria-hidden="true" />
                                )}
                                <div className="estado-timeline-body">
                                    <div className="estado-timeline-header">
                                        {h.estado_anterior && (
                                            <span className="estado-timeline-prev">{h.estado_anterior}</span>
                                        )}
                                        {h.estado_anterior && <span className="estado-timeline-arrow">→</span>}
                                        <span
                                            className="estado-timeline-new"
                                            style={{ color: getEstadoColor(h.estado_nuevo) }}
                                        >
                                            {h.estado_nuevo}
                                        </span>
                                    </div>
                                    <div className="estado-timeline-meta">
                                        {h.cambiado_por_nombre || 'Usuario eliminado'} · {formatDateTimeShort(h.cambiado_at)}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </Modal>

            {/* Modal read-only: detalle de variaciones aplicadas a una fila/bloque/servicio.
                Reusa VariacionesModal con readOnly=true para no permitir edicion desde el detalle. */}
            <VariacionesModal
                open={!!variacionesReadModal.open}
                onClose={() => setVariacionesReadModal({ open: false })}
                base={variacionesReadModal.base || 0}
                variaciones={variacionesReadModal.variaciones || []}
                onChange={() => {}}
                titulo={variacionesReadModal.titulo || 'Variaciones aplicadas'}
                moneda={moneda}
                readOnly={true}
            />

            {/* Modal completo del hilo de versiones — se abre desde el boton
                "Ver todas" del card resumen. Permite marcar principal, navegar
                a otras versiones, etc. */}
            <HiloVersionesModal
                open={hiloModal}
                onClose={() => setHiloModal(false)}
                cotizacionId={id}
                onChange={() => { cargarCotizacion({ silent: true }); cargarHilo(); }}
            />
        </div>
    );
};

export default CotizacionDetalle;
